import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { profileAPI, reviewAPI, dienstleisterAPI } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, Star, User, Building2, Phone,
  Globe, Briefcase, MessageSquare, Award, Shield,
  TreeDeciduous, Calendar, Ruler, ChevronDown, ChevronUp,
  Send, CheckCircle2, Clock, Mail, Truck,
} from 'lucide-react';

const RatingBar = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-gray-500 w-32 shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all"
          style={{ width: `${(value / 5) * 100}%` }}
        />
      </div>
      <span className="text-sm font-medium text-gray-700 w-8 text-right">{value}/5</span>
    </div>
  );
};

const StarRatingInput = ({ value, onChange, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'lg' ? 'h-7 w-7' : 'h-5 w-5';
  return (
    <div className="flex gap-1" data-testid="star-rating-input">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
          className="transition-transform hover:scale-110"
          data-testid={`star-input-${star}`}
        >
          <Star
            className={`${sizeClass} transition-colors ${
              star <= (hover || value)
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-200 hover:text-amber-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const renderStars = (rating, size = 'sm') => {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'md' ? 'h-5 w-5' : 'h-6 w-6';
  return (
    <div className="flex items-center gap-0.5" data-testid="star-rating-display">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${sizeClass} ${
            star <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'
          }`}
        />
      ))}
    </div>
  );
};

const AnbieterDetailPage = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [referenzen, setReferenzen] = useState([]);
  const [bewertungen, setBewertungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBewertungForm, setShowBewertungForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [bewertungForm, setBewertungForm] = useState({
    rating: 0, kommentar: '', qualitaet: 0, kommunikation: 0,
    zuverlaessigkeit: 0, reviewer_name: '', reviewer_firma: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    try {
      const [profileRes, reviewsRes] = await Promise.all([
        profileAPI.getProfile(id),
        reviewAPI.getForUser(id).catch(() => ({ data: [] })),
      ]);
      setProfile(profileRes.data);
      setReviews(reviewsRes.data || []);

      const profilId = profileRes.data.profil_id || profileRes.data.user_id || id;
      const [refRes, bewRes] = await Promise.all([
        dienstleisterAPI.referenzen(profilId).catch(() => ({ data: { referenzen: [] } })),
        dienstleisterAPI.bewertungen(profilId).catch(() => ({ data: { bewertungen: [] } })),
      ]);
      setReferenzen(refRes.data?.referenzen || []);
      setBewertungen(bewRes.data?.bewertungen || []);
    } catch (error) {
      console.error('Error fetching provider:', error);
      toast.error(t('anbieterDetail.nichtGefunden'));
    } finally {
      setLoading(false);
    }
  };

  const submitBewertung = async () => {
    if (!bewertungForm.rating || !bewertungForm.kommentar || !bewertungForm.reviewer_name) {
      toast.error(t('anbieterDetail.pflichtfelder'));
      return;
    }
    setSending(true);
    try {
      const profilId = profile.profil_id || profile.user_id || id;
      const payload = {
        ...bewertungForm,
        qualitaet: bewertungForm.qualitaet || null,
        kommunikation: bewertungForm.kommunikation || null,
        zuverlaessigkeit: bewertungForm.zuverlaessigkeit || null,
      };
      await dienstleisterAPI.bewertungAbgeben(profilId, payload);
      toast.success(t('anbieterDetail.bewertungGespeichert'));
      setShowBewertungForm(false);
      setBewertungForm({ rating: 0, kommentar: '', qualitaet: 0, kommunikation: 0, zuverlaessigkeit: 0, reviewer_name: '', reviewer_firma: '' });
      fetchData();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && detail.includes('Referenz')) {
        toast.error(detail);
      } else {
        toast.error(t('anbieterDetail.bewertungFehler'));
      }
    } finally {
      setSending(false);
    }
  };

  const alleBewertungen = [
    ...bewertungen.map(b => ({ ...b, source: 'direct' })),
    ...reviews.map(r => ({ ...r, source: 'project', reviewer_name: r.reviewer_name })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-primary">{t('anbieterDetail.laden')}</div>
        </div>
      </AppShell>
    );
  }

  if (!profile) return null;

  const zertifizierungen = profile.zertifizierungen || [];
  const beschreibung = profile.description || profile.beschreibung || '';
  const leistungen = profile.services || profile.leistungen || [];
  const kontaktEmail = profile.kontakt_email || profile.user_email || '';
  const kontaktTelefon = profile.phone || profile.kontakt_telefon || '';

  return (
    <AppShell>
      <div className="space-y-6" data-testid="anbieter-detail-page">
        {/* Back Link */}
        <Link
          to="/anbieter"
          className="inline-flex items-center gap-2 text-gray-500 hover:text-primary transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('anbieterDetail.zurueck')}
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Header */}
            <Card data-testid="profile-header-card">
              <CardContent className="p-6">
                <div className="flex items-start gap-5">
                  <div className="w-20 h-20 rounded-2xl bg-primary/8 flex items-center justify-center flex-shrink-0">
                    {profile.company_name ? (
                      <Building2 className="h-10 w-10 text-primary" />
                    ) : (
                      <User className="h-10 w-10 text-primary" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h1 className="font-serif text-2xl font-bold text-gray-900" data-testid="provider-name">
                        {profile.company_name || profile.user_name}
                      </h1>
                      {profile.is_demo && (
                        <Badge variant="outline" className="text-xs text-gray-400 border-gray-200">{t('anbieterDetail.beispielProfil')}</Badge>
                      )}
                    </div>
                    {profile.company_name && profile.user_name && (
                      <p className="text-gray-500 mt-0.5">{profile.user_name}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 mt-3">
                      {(profile.bundesland || profile.ort) && (
                        <p className="text-sm text-gray-500 flex items-center gap-1" data-testid="provider-location">
                          <MapPin className="h-4 w-4" />
                          {profile.ort ? `${profile.ort}, ` : ''}{profile.bundesland}
                        </p>
                      )}
                      {profile.experience_years && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Briefcase className="h-4 w-4" />
                          {profile.experience_years} {t('anbieterDetail.jahreErfahrung')}
                        </p>
                      )}
                      {profile.country && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Globe className="h-4 w-4" />
                          {profile.country}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 mt-4" data-testid="provider-rating">
                      {renderStars(profile.avg_rating || 0, 'md')}
                      <span className="text-lg font-semibold text-gray-700 ml-1">
                        {(profile.avg_rating || 0).toFixed(1)}
                      </span>
                      <span className="text-gray-400">
                        ({alleBewertungen.length || profile.review_count || 0} {t('anbieterDetail.bewertungen')})
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            {beschreibung && (
              <Card data-testid="description-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TreeDeciduous className="h-5 w-5 text-primary" />
                    {t('anbieterDetail.ueberUns')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-gray-600 whitespace-pre-wrap leading-relaxed ${!showFullDesc && beschreibung.length > 400 ? 'line-clamp-4' : ''}`}>
                    {beschreibung}
                  </p>
                  {beschreibung.length > 400 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFullDesc(!showFullDesc)}
                      className="mt-2 text-primary text-xs"
                      data-testid="toggle-description"
                    >
                      {showFullDesc ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
                      {showFullDesc ? t('anbieterDetail.schliessen') : 'Mehr...'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Services */}
            {leistungen.length > 0 && (
              <Card data-testid="services-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-primary" />
                    {t('anbieterDetail.dienstleistungen')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {leistungen.map((service) => (
                      <Badge key={service} variant="secondary" className="bg-primary/8 text-primary px-3 py-1.5 text-sm">
                        {service}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Maschinenpark / Equipment (if available) */}
            {profile.maschinenpark && profile.maschinenpark.length > 0 && (
              <Card data-testid="equipment-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="h-5 w-5 text-primary" />
                    Maschinenpark
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.maschinenpark.map((m, i) => (
                      <Badge key={i} variant="outline" className="px-3 py-1.5 text-sm border-warm-200">
                        {m}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {zertifizierungen.length > 0 && (
              <Card data-testid="certifications-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Award className="h-5 w-5 text-emerald-600" />
                    {t('anbieterDetail.zertifizierungen')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {zertifizierungen.map((z, i) => (
                      <Badge key={i} className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 text-sm">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                        {z}
                      </Badge>
                    ))}
                  </div>
                  {profile.zertifizierungen_freitext && (
                    <p className="text-sm text-gray-500 mt-3">{profile.zertifizierungen_freitext}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Service Regions */}
            {profile.service_regions?.length > 0 && (
              <Card data-testid="regions-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    {t('anbieterDetail.einsatzgebiete')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {profile.service_regions.map((region) => (
                      <Badge key={region} variant="outline" className="px-3 py-1.5 text-sm">
                        <MapPin className="h-3 w-3 mr-1" />
                        {region}
                      </Badge>
                    ))}
                  </div>
                  {profile.service_radius_km && (
                    <p className="text-sm text-gray-500 mt-3 flex items-center gap-1">
                      <Ruler className="h-4 w-4" />
                      {t('anbieterDetail.einsatzradius')}: {profile.service_radius_km} km
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Referenzen */}
            <Card data-testid="references-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  {t('anbieterDetail.referenzen')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {referenzen.length === 0 ? (
                  <p className="text-gray-400 text-center py-6" data-testid="no-references">
                    {t('anbieterDetail.keineReferenzen')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {referenzen.map((ref) => (
                      <div key={ref.id} className="p-4 rounded-lg border border-warm-200 bg-warm-50/50" data-testid={`referenz-${ref.id}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{ref.projekt_name}</h4>
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-gray-500">
                              {ref.jahr && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3.5 w-3.5" /> {ref.jahr}
                                </span>
                              )}
                              {ref.region && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="h-3.5 w-3.5" /> {ref.region}
                                </span>
                              )}
                              {ref.flaeche_ha && (
                                <span className="flex items-center gap-1">
                                  <Ruler className="h-3.5 w-3.5" /> {ref.flaeche_ha} ha
                                </span>
                              )}
                            </div>
                          </div>
                          {ref.auftraggeber && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              {ref.auftraggeber}
                            </Badge>
                          )}
                        </div>
                        {ref.beschreibung && (
                          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{ref.beschreibung}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bewertungen */}
            <Card data-testid="reviews-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Star className="h-5 w-5 text-amber-400" />
                    {t('anbieterDetail.bewertungen')} ({alleBewertungen.length})
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBewertungForm(!showBewertungForm)}
                    data-testid="toggle-review-form"
                  >
                    {showBewertungForm ? (
                      <><ChevronUp className="h-4 w-4 mr-1" /> {t('anbieterDetail.schliessen')}</>
                    ) : (
                      <><Send className="h-4 w-4 mr-1" /> {t('anbieterDetail.bewertungSchreiben')}</>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Bewertungsformular */}
                {showBewertungForm && (
                  <div className="mb-6 p-5 rounded-lg border-2 border-primary/15 bg-primary/[0.02]" data-testid="review-form">
                    <h4 className="font-semibold text-gray-900 mb-4">{t('anbieterDetail.ihreBewertung')}</h4>
                    {referenzen.length < 1 && (
                      <div className="mb-4 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="review-plausibility-hint">
                        <p className="text-sm text-amber-800">
                          {t('anbieterDetail.bewertungPlausibilitaet')}
                        </p>
                      </div>
                    )}
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 block">{t('anbieterDetail.gesamtbewertung')} *</label>
                        <StarRatingInput
                          value={bewertungForm.rating}
                          onChange={(v) => setBewertungForm(p => ({ ...p, rating: v }))}
                          size="lg"
                        />
                      </div>

                      <div className="grid sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('anbieterDetail.qualitaet')}</label>
                          <StarRatingInput
                            value={bewertungForm.qualitaet}
                            onChange={(v) => setBewertungForm(p => ({ ...p, qualitaet: v }))}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('anbieterDetail.kommunikation')}</label>
                          <StarRatingInput
                            value={bewertungForm.kommunikation}
                            onChange={(v) => setBewertungForm(p => ({ ...p, kommunikation: v }))}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('anbieterDetail.zuverlaessigkeit')}</label>
                          <StarRatingInput
                            value={bewertungForm.zuverlaessigkeit}
                            onChange={(v) => setBewertungForm(p => ({ ...p, zuverlaessigkeit: v }))}
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t('anbieterDetail.erfahrungsbericht')} *</label>
                        <Textarea
                          value={bewertungForm.kommentar}
                          onChange={(e) => setBewertungForm(p => ({ ...p, kommentar: e.target.value }))}
                          placeholder={t('anbieterDetail.erfahrungsberichtPlaceholder')}
                          className="min-h-[100px]"
                          data-testid="review-comment-input"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t('anbieterDetail.ihrName')} *</label>
                          <Input
                            value={bewertungForm.reviewer_name}
                            onChange={(e) => setBewertungForm(p => ({ ...p, reviewer_name: e.target.value }))}
                            placeholder={t('anbieterDetail.namePlaceholder')}
                            data-testid="review-name-input"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-700 mb-1.5 block">{t('anbieterDetail.firmaLabel')}</label>
                          <Input
                            value={bewertungForm.reviewer_firma}
                            onChange={(e) => setBewertungForm(p => ({ ...p, reviewer_firma: e.target.value }))}
                            placeholder={t('anbieterDetail.firmaPlaceholder')}
                            data-testid="review-company-input"
                          />
                        </div>
                      </div>

                      <Button
                        onClick={submitBewertung}
                        disabled={sending || referenzen.length < 1}
                        className="w-full"
                        data-testid="submit-review-btn"
                      >
                        {sending ? t('anbieterDetail.wirdGespeichert') : referenzen.length < 1 ? t('anbieterDetail.erstNachAuftrag') : t('anbieterDetail.absenden')}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Aggregate Ratings */}
                {alleBewertungen.length > 0 && (
                  <div className="mb-6 p-4 rounded-lg bg-warm-50 border border-warm-200" data-testid="rating-summary">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-900">
                          {(profile.avg_rating || 0).toFixed(1)}
                        </div>
                        <div className="mt-1">{renderStars(profile.avg_rating || 0, 'sm')}</div>
                        <div className="text-xs text-gray-400 mt-1">{alleBewertungen.length} {t('anbieterDetail.bewertungen')}</div>
                      </div>
                      <div className="flex-1 space-y-1.5">
                        {(() => {
                          const quals = alleBewertungen.filter(b => b.qualitaet).map(b => b.qualitaet);
                          const komms = alleBewertungen.filter(b => b.kommunikation).map(b => b.kommunikation);
                          const zuves = alleBewertungen.filter(b => b.zuverlaessigkeit).map(b => b.zuverlaessigkeit);
                          return (
                            <>
                              {quals.length > 0 && <RatingBar label={t('anbieterDetail.qualitaet')} value={Math.round((quals.reduce((a, b) => a + b, 0) / quals.length) * 10) / 10} />}
                              {komms.length > 0 && <RatingBar label={t('anbieterDetail.kommunikation')} value={Math.round((komms.reduce((a, b) => a + b, 0) / komms.length) * 10) / 10} />}
                              {zuves.length > 0 && <RatingBar label={t('anbieterDetail.zuverlaessigkeit')} value={Math.round((zuves.reduce((a, b) => a + b, 0) / zuves.length) * 10) / 10} />}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Review List */}
                {alleBewertungen.length === 0 ? (
                  <p className="text-gray-400 text-center py-6" data-testid="no-reviews">
                    {t('anbieterDetail.keineBewertungen')}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {alleBewertungen.map((review, idx) => (
                      <div key={review.id || idx} className="p-4 rounded-lg border border-warm-200" data-testid={`review-${review.id || idx}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              {renderStars(review.rating, 'sm')}
                              <span className="text-sm font-medium text-gray-700">
                                {review.reviewer_name}
                              </span>
                              {review.reviewer_firma && (
                                <span className="text-xs text-gray-400">
                                  ({review.reviewer_firma})
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {review.source === 'project' && (
                              <Badge variant="outline" className="text-[10px]">{t('anbieterDetail.projektBadge')}</Badge>
                            )}
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(review.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{review.kommentar}</p>

                        {(review.qualitaet || review.kommunikation || review.zuverlaessigkeit) && (
                          <div className="flex flex-wrap gap-4 mt-3 pt-3 border-t border-warm-200 text-xs text-gray-500">
                            {review.qualitaet && <span>{t('anbieterDetail.qualitaet')}: <strong>{review.qualitaet}/5</strong></span>}
                            {review.kommunikation && <span>{t('anbieterDetail.kommunikation')}: <strong>{review.kommunikation}/5</strong></span>}
                            {review.zuverlaessigkeit && <span>{t('anbieterDetail.zuverlaessigkeit')}: <strong>{review.zuverlaessigkeit}/5</strong></span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card data-testid="contact-card">
              <CardHeader>
                <CardTitle className="text-lg">{t('anbieterDetail.kontakt')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Link to={`/nachrichten/${profile.user_id}`}>
                  <Button className="w-full" data-testid="send-message-btn">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {t('anbieterDetail.nachrichtSenden')}
                  </Button>
                </Link>

                {kontaktTelefon && (
                  <a
                    href={`tel:${kontaktTelefon}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/5"
                    data-testid="phone-link"
                  >
                    <Phone className="h-4 w-4" />
                    {kontaktTelefon}
                  </a>
                )}

                {kontaktEmail && (
                  <a
                    href={`mailto:${kontaktEmail}`}
                    className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/5"
                    data-testid="email-link"
                  >
                    <Mail className="h-4 w-4" />
                    {kontaktEmail}
                  </a>
                )}

                {profile.website && (
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/5"
                    data-testid="website-link"
                  >
                    <Globe className="h-4 w-4" />
                    {t('anbieterDetail.websiteLabel')}
                  </a>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card data-testid="quick-stats-card">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">{t('anbieterDetail.aufEinenBlick')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">{t('anbieterDetail.bewertungLabel')}</span>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      <span className="font-semibold">{(profile.avg_rating || 0).toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">{t('anbieterDetail.bewertungen')}</span>
                    <span className="font-semibold">{alleBewertungen.length || profile.review_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">{t('anbieterDetail.referenzen')}</span>
                    <span className="font-semibold">{referenzen.length}</span>
                  </div>
                  {profile.experience_years && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">{t('anbieterDetail.erfahrung')}</span>
                      <span className="font-semibold">{profile.experience_years} {t('anbieterDetail.jahre')}</span>
                    </div>
                  )}
                  {profile.service_radius_km && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">{t('anbieterDetail.einsatzradius')}</span>
                      <span className="font-semibold">{profile.service_radius_km} km</span>
                    </div>
                  )}
                  {zertifizierungen.length > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-sm">{t('anbieterDetail.zertifizierungen')}</span>
                      <span className="font-semibold">{zertifizierungen.length}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Demo Notice */}
            {profile.is_demo && (
              <Card className="border-amber-200 bg-amber-50" data-testid="demo-notice">
                <CardContent className="p-4 text-center">
                  <p className="text-sm text-amber-700">
                    {t('anbieterDetail.beispielHinweis')}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default AnbieterDetailPage;
